/**
 * Data Sources API endpoints
 */
import type {
    ApiResponse,
    ApiListResponse,
    DataSource,
    DataSourceInput,
    DataSourceUpdate,
    TableSchema,
    ExclusionConfig,
    FormInclusionConfig,
} from "../lib/types";
import { authenticatedRequest } from "../lib/api";

export async function getDataSources(token: string, companyId?: string): Promise<DataSource[]> {
    const url = companyId ? `/api/data-sources?companyId=${companyId}` : "/api/data-sources";
    return authenticatedRequest<ApiListResponse<DataSource>>(
        url,
        token
    ).then((res) => res.data);
}

export async function createDataSource(
    token: string,
    input: DataSourceInput
): Promise<DataSource> {
    return authenticatedRequest<ApiResponse<DataSource>>(
        "/api/data-sources",
        token,
        {
            method: "POST",
            body: JSON.stringify(input),
        }
    ).then((res) => res.data);
}

export async function getSchema(
    token: string,
    id: string,
    companyId?: string
): Promise<TableSchema[]> {
    const url = companyId ? `/api/data-sources/${id}/schema?companyId=${companyId}` : `/api/data-sources/${id}/schema`;
    return authenticatedRequest<ApiResponse<TableSchema[]>>(
        url,
        token
    ).then((res) => res.data);
}

export async function updateDataSourceExclusions(
    token: string,
    id: string,
    config: ExclusionConfig,
    companyId?: string
): Promise<void> {
    const url = companyId ? `/api/data-sources/${id}/exclusions?companyId=${companyId}` : `/api/data-sources/${id}/exclusions`;
    return authenticatedRequest(url, token, {
        method: "PATCH",
        body: JSON.stringify(config),
    }).then(() => undefined);
}

export async function updateDataSourceFormInclusions(
    token: string,
    id: string,
    config: FormInclusionConfig,
    companyId?: string
): Promise<void> {
    const url = companyId ? `/api/data-sources/${id}/form-inclusions?companyId=${companyId}` : `/api/data-sources/${id}/form-inclusions`;
    return authenticatedRequest(url, token, {
        method: "PATCH",
        body: JSON.stringify(config),
    }).then(() => undefined);
}
export async function deleteDataSource(token: string, id: string, companyId?: string): Promise<void> {
    const url = companyId ? `/api/data-sources/${id}?companyId=${companyId}` : `/api/data-sources/${id}`;
    return authenticatedRequest(url, token, {
        method: "DELETE",
    }).then(() => undefined);
}

export async function updateDataSource(
    token: string,
    id: string,
    update: DataSourceUpdate & { companyId?: string }
): Promise<DataSource> {
    const url = update.companyId ? `/api/data-sources/${id}?companyId=${update.companyId}` : `/api/data-sources/${id}`;
    return authenticatedRequest<ApiResponse<DataSource>>(
        url,
        token,
        {
            method: "PUT",
            body: JSON.stringify(update),
        }
    ).then((res) => res.data);
}

export async function savePlaygroundData(
    token: string,
    id: string,
    tableName: string,
    data: any[],
    companyId?: string
): Promise<{ tableName: string; rowCount: number }> {
    const url = companyId ? `/api/data-sources/${id}/save-playground-data?companyId=${companyId}` : `/api/data-sources/${id}/save-playground-data`;
    return authenticatedRequest<ApiResponse<{ tableName: string; rowCount: number }>>(
        url,
        token,
        {
            method: "POST",
            body: JSON.stringify({ tableName, data, companyId }),
        }
    ).then((res) => res.data);
}

export async function testDataSourceConnection(
    token: string,
    input: { type: string; config: any }
): Promise<void> {
    return authenticatedRequest(
        "/api/data-sources/test-connection",
        token,
        {
            method: "POST",
            body: JSON.stringify(input),
        }
    ).then(() => undefined);
}

export async function getDataSourceSchemas(
    token: string,
    id: string,
    companyId?: string
): Promise<string[]> {
    const url = companyId ? `/api/data-sources/${id}/structure/schemas?companyId=${companyId}` : `/api/data-sources/${id}/structure/schemas`;
    return authenticatedRequest<ApiResponse<string[]>>(
        url,
        token
    ).then((res) => res.data);
}

export async function getDataSourceTables(
    token: string,
    id: string,
    schema: string,
    companyId?: string
): Promise<{ name: string }[]> {
    const url = companyId
        ? `/api/data-sources/${id}/structure/tables?schema=${schema}&companyId=${companyId}`
        : `/api/data-sources/${id}/structure/tables?schema=${schema}`;
    return authenticatedRequest<ApiResponse<{ name: string }[]>>(
        url,
        token
    ).then((res) => res.data);
}

export async function getDataSourceColumns(
    token: string,
    id: string,
    schema: string,
    table: string,
    companyId?: string
): Promise<string[]> {
    const url = companyId
        ? `/api/data-sources/${id}/structure/columns?schema=${schema}&table=${table}&companyId=${companyId}`
        : `/api/data-sources/${id}/structure/columns?schema=${schema}&table=${table}`;
    return authenticatedRequest<ApiResponse<string[]>>(
        url,
        token
    ).then((res) => res.data);
}
